// A2-592 — "Keep as a business: record the evidence" for a customer whose VAT
// number the EU check rejects (operator approved 2026-10-28; IR 282/2011
// Arts 17-18). The evidence is kept against the NUMBER, and the next re-run treats
// the number as valid for invoices from the chosen date, so every row with it
// follows (api/routes_vat_number_evidence, engine/vat_number_evidence).
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { PresetReasonSelect } from "../PresetReasonSelect";
import { BUSINESS_EVIDENCE_REASONS, EVIDENCE_WITHDRAW_REASONS } from "../../lib/reviewReasonPresets";

const TYPES = {
  // A2-625 (operator 2026-10-31: "operator can just select evidence provided
  // and this should suffice") — first and the default; no reference needed.
  EVIDENCE_HELD: "Evidence that the customer is in business is held on file",
  CERTIFICATE_OF_STATUS: "Certificate from the customer's tax authority that it is in business",
  VALID_ON_INVOICE_DATE: "Proof the number was valid on the invoice dates (an earlier EU check)",
  NUMBER_NEWLY_ISSUED: "The customer has applied for a number and not yet received it (proof of the application)",
};
// Proof about past dates covers those dates only (engine/vat_number_evidence).
const NEEDS_END_DATE = new Set(["VALID_ON_INVOICE_DATE", "NUMBER_NEWLY_ISSUED"]);
const REF_OPTIONAL = new Set(["EVIDENCE_HELD"]);
const FIELD = "w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-xs";

// One row (`tid`), the ticked rows (`tids`), or every open row of the card
// (`allOpen` + `flagType`) — A2-625: one record per number, the same evidence.
export const BusinessEvidence = ({ tid, tids, allOpen, flagType, label, calcId, invoiceDate, calcFrozen, onResolved }) => {
  const { t } = useTranslation();
  const many = allOpen || (tids && tids.length > 1);
  const key = allOpen ? "all" : many ? "ticked" : (tid || (tids || [])[0]);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("EVIDENCE_HELD");
  const [ref, setRef] = useState("");
  const [from, setFrom] = useState((invoiceDate || "").slice(0, 10));
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const isDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d);
  const valid = !!type && (REF_OPTIONAL.has(type) || ref.trim().length >= 3)
    && (many ? (!from || isDate(from)) : isDate(from)) && reason.trim().length >= 8
    && (!NEEDS_END_DATE.has(type) || (isDate(to) && to >= from)) && (!to || (isDate(to) && to >= from));

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      if (many) {
        // saved for the next re-run, like every Step 4 answer (SavedAnswersBar)
        const res = await authClient.post(`/runs/calc/${calcId}/vat-number-evidence/bulk`, {
          ...(allOpen ? { all_open: true, flag_type: flagType } : { txn_ids: tids }),
          evidence_type: type, evidence_ref: ref.trim(), valid_from: from, valid_to: to, reason: reason.trim(),
        });
        setOpen(false);
        toast.success(t("ui.review_answers.evidence_saved_many", { n: res?.data?.rows ?? (tids || []).length,
          defaultValue: "Evidence recorded for {{n}} row(s) — applied when the calculation re-runs" }));
        if (onResolved) await onResolved();
        return;
      }
      await authClient.post(`/runs/calc/${calcId}/vat-number-evidence`, {
        txn_id: tid, evidence_type: type, evidence_ref: ref.trim(), valid_from: from, valid_to: to, reason: reason.trim(),
      });
      setOpen(false);
      toast.success(t("ui.review_answers.evidence_saved", { defaultValue: "Evidence recorded — figures change at the next re-run" }));
      // A1-105 — saved for the one re-run, like every Step 4 answer
      if (onResolved) await onResolved();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (calcFrozen) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`bev-open-${key}`}
                className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800">
          {label || t("ui.review_answers.evidence_open", { defaultValue: "Keep as a business: record the evidence →" })}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid={`bev-popover-${key}`}
                      className="w-96 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2">
        <p role="note" className="text-xs text-amber-800 dark:text-amber-200">
          {t("ui.review_answers.evidence_warning", { defaultValue:
            "The EU check says this number is not valid for trade between EU countries. Keep the evidence on file: a supplier who ignores facts pointing the other way can lose business treatment (IR 282/2011 Art 18, \"unless he has information to the contrary\"). Ask your adviser if unsure." })}
        </p>
        <p data-testid={`bev-normally-${key}`} className="text-[11px] text-slate-600 dark:text-slate-300">
          {t("ui.review_answers.evidence_normally", { defaultValue:
            "Normally expected: a valid VAT number; otherwise other proof the customer is in business, such as a certificate from its tax authority or commercial documents like a contract (HMRC Notice 741A §6.3; EU Reg 282/2011 Art 18)." })}
        </p>
        <select data-testid={`bev-type-${key}`} value={type} onChange={(e) => { setType(e.target.value); setPreset(""); setReason(""); }} className={FIELD}
                aria-label={t("ui.review_answers.evidence_type", { defaultValue: "Kind of evidence" })}>
          <option value="">{t("ui.review_answers.evidence_type_pick", { defaultValue: "Pick the kind of evidence…" })}</option>
          {Object.entries(TYPES).map(([k, v]) => (
            <option key={k} value={k}>{t(`ui.review_answers.evidence_types.${k}`, { defaultValue: v })}</option>
          ))}
        </select>
        <label className="block text-xs text-slate-700 dark:text-slate-200">
          {REF_OPTIONAL.has(type)
            ? t("ui.review_answers.evidence_ref_optional", { defaultValue: "The document's reference (optional)" })
            : t("ui.review_answers.evidence_ref", { defaultValue: "The document's reference" })}
          <input data-testid={`bev-ref-${key}`} value={ref} onChange={(e) => setRef(e.target.value)}
                 autoComplete="off" spellCheck={false} placeholder={t("ui.review_answers.evidence_ref_example", { defaultValue: "e.g. certificate no. 2026/0412…" })} className={FIELD} />
        </label>
        <label className="block text-xs text-slate-700 dark:text-slate-200">
          {many
            ? t("ui.review_answers.evidence_from_many", { defaultValue: "Covers invoices from (empty: the earliest of these rows)" })
            : t("ui.review_answers.evidence_from", { defaultValue: "Covers invoices from" })}
          <input type="date" data-testid={`bev-from-${key}`} value={from} onChange={(e) => setFrom(e.target.value)} className={FIELD} />
        </label>
        <label className="block text-xs text-slate-700 dark:text-slate-200">
          {NEEDS_END_DATE.has(type)
            ? t("ui.review_answers.evidence_to_required", { defaultValue: "Last invoice date it covers" })
            : t("ui.review_answers.evidence_to", { defaultValue: "Last invoice date it covers (leave empty: until withdrawn)" })}
          <input type="date" data-testid={`bev-to-${key}`} value={to} onChange={(e) => setTo(e.target.value)} className={FIELD} />
        </label>
        <PresetReasonSelect testid={`bev-reason-${key}`} presets={BUSINESS_EVIDENCE_REASONS[type] || []}
                            preset={preset} onPick={(p, v) => { setPreset(p); setReason(v); }} />
        {err && <div data-testid={`bev-error-${key}`} className="text-xs text-red-700 dark:text-red-300">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setOpen(false)} className="text-xs underline text-slate-600 dark:text-slate-300">
            {t("ui.review_grouped.sc_cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" data-testid={`bev-submit-${key}`} disabled={busy || !valid} onClick={submit}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/vat-number-evidence"
                  className="text-xs font-semibold px-3 py-1 rounded bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white">
            {busy ? t("ui.review_grouped.sc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_answers.evidence_submit", { defaultValue: "Save" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// A2-592 — end the evidence for this row's number (Art 18: "unless he has
// information to the contrary"); the record is kept, marked withdrawn.
export const EvidenceWithdraw = ({ tid, calcId, calcFrozen, onResolved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    if (reason.trim().length < 8 || busy) return;
    setBusy(true); setErr(null);
    try {
      await authClient.post(`/runs/calc/${calcId}/vat-number-evidence/withdraw`, { txn_id: tid, reason: reason.trim() });
      setOpen(false);
      toast.success(t("ui.review_answers.evidence_withdrawn", { defaultValue: "Evidence withdrawn — figures change at the next re-run" }));
      if (onResolved) await onResolved();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  if (calcFrozen) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`bevw-open-${tid}`}
                className="text-xs px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800">
          {t("ui.review_answers.evidence_withdraw_open", { defaultValue: "Withdraw the evidence →" })}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid={`bevw-popover-${tid}`}
                      className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2">
        <PresetReasonSelect testid={`bevw-reason-${tid}`} presets={EVIDENCE_WITHDRAW_REASONS}
                            preset={preset} onPick={(p, v) => { setPreset(p); setReason(v); }} />
        {err && <div data-testid={`bevw-error-${tid}`} className="text-xs text-red-700 dark:text-red-300">{err}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setOpen(false)} className="text-xs underline text-slate-600 dark:text-slate-300">
            {t("ui.review_grouped.sc_cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" data-testid={`bevw-submit-${tid}`} disabled={busy || reason.trim().length < 8} onClick={submit}
                  data-resolution-class="self-resolve"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/vat-number-evidence/withdraw"
                  className="text-xs font-semibold px-3 py-1 rounded bg-teal-700 hover:bg-teal-800 disabled:opacity-50 text-white">
            {busy ? t("ui.review_grouped.sc_submitting", { defaultValue: "Saving…" })
                  : t("ui.review_answers.evidence_withdraw_submit", { defaultValue: "Withdraw" })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BusinessEvidence;
